import { useState, useEffect } from 'react';
import { OKR, KeyResult, Departamento, OKRStatus } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Plus, Target, Trash2, ChevronDown, ChevronUp, History,
  Calendar, Clock, CheckCircle, XCircle, ArrowUp, ArrowDown, Paperclip, Send,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getProgressIndicatorStyle, getDeadlineStatus, getDeadlineBadgeStyle } from '@/lib/progress-utils';
import { formatDateOnly } from '@/lib/dateOnly';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const statusLabels: Record<OKRStatus, string> = {
  a_fazer: 'A Fazer',
  fazendo: 'Fazendo',
  feito: 'Feito',
  pausado: 'Pausado',
  cancelado: 'Cancelado',
};

const statusStyles: Record<OKRStatus, string> = {
  a_fazer: 'status-todo',
  fazendo: 'status-doing',
  feito: 'status-done',
  pausado: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  cancelado: 'bg-red-100 text-red-800',
};

function getPriorityLabel(index: number) {
  if (index === 0) return { label: 'Alta', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  if (index === 1) return { label: 'Média', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' };
  if (index === 2) return { label: 'Normal', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' };
  return null;
}

function getApprovalStatusBadge(kr: KeyResult) {
  if (!kr.aprovacao) return null;
  switch (kr.aprovacao.status) {
    case 'pending':
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 gap-1">
          <Clock className="h-3 w-3" />
          Aguardando aprovação
        </Badge>
      );
    case 'rejected':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 gap-1" title={kr.aprovacao.motivo_rejeicao}>
          <XCircle className="h-3 w-3" />
          Rejeitado
        </Badge>
      );
    case 'approved':
      return (
        <Badge variant="outline" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 gap-1">
          <CheckCircle className="h-3 w-3" />
          Aprovado
        </Badge>
      );
    default:
      return null;
  }
}

export interface OKRListViewProps {
  okrs: OKR[];
  keyResults: KeyResult[];
  departamentos: Departamento[];
  isAdmin: boolean;
  onEditOKR: (okr: OKR) => void;
  onDeleteOKR: (id: string) => void;
  onOpenHistorico: (okr: OKR) => void;
  onAddKR: (okrId: string) => void;
  onEditKR: (kr: KeyResult, okrId: string) => void;
  onDeleteKR: (krId: string) => void;
  onReorderKR: (okrId: string, krId: string, direction: 'up' | 'down') => void;
  onRequestCompletion: (kr: KeyResult) => void;
  hasActiveFilters: boolean;
  allExpanded?: boolean;
}

export function OKRListView({
  okrs,
  keyResults,
  departamentos,
  isAdmin,
  onEditOKR,
  onDeleteOKR,
  onOpenHistorico,
  onAddKR,
  onEditKR,
  onDeleteKR,
  onReorderKR,
  onRequestCompletion,
  hasActiveFilters,
  allExpanded,
}: OKRListViewProps) {
  const [expandedOKRs, setExpandedOKRs] = useState<Set<string>>(new Set());

  // Botão global "ver/ocultar todos os Key Results" na toolbar — mesmo padrão do Kanban:
  // sincroniza todos os cards com o toggle geral, sem impedir que o usuário ainda
  // expanda/recolha um card individual depois (o efeito só reage quando o valor global muda).
  useEffect(() => {
    if (allExpanded === undefined) return;
    setExpandedOKRs(allExpanded ? new Set(okrs.map((okr) => okr.id)) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExpanded]);

  const toggleExpanded = (okrId: string) => {
    setExpandedOKRs((prev) => {
      const next = new Set(prev);
      if (next.has(okrId)) next.delete(okrId);
      else next.add(okrId);
      return next;
    });
  };

  const getKRsForOKR = (okrId: string) =>
    keyResults.filter((kr) => kr.okr_id === okrId).sort((a, b) => a.ordem - b.ordem);
  const getDepartamento = (depId: string) => departamentos.find((d) => d.id === depId);

  if (okrs.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        {hasActiveFilters ? 'Nenhuma OKR encontrada com os filtros aplicados' : (
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">Nenhuma OKR cadastrada</p>
            {isAdmin && (
              <p className="text-sm text-muted-foreground mt-2">Use o botão "Nova OKR" para começar</p>
            )}
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {okrs.map((okr) => {
        const krs = getKRsForOKR(okr.id);
        const dep = getDepartamento(okr.departamento_id);
        const isExpanded = expandedOKRs.has(okr.id);

        return (
          <Card
            key={okr.id}
            className={cn('card-interactive overflow-hidden', isAdmin && 'cursor-pointer')}
            onClick={isAdmin ? () => onEditOKR(okr) : undefined}
          >
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary flex-shrink-0" />
                  <CardTitle className="text-base md:text-lg line-clamp-2">{okr.titulo}</CardTitle>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge className={statusStyles[okr.status]}>{statusLabels[okr.status]}</Badge>
                  {dep && <Badge variant="outline">{dep.nome}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground line-clamp-2">{okr.descricao}</p>
                {(okr.data_inicio || okr.data_fim) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {okr.data_inicio && (
                      <Badge variant="outline" className="text-xs gap-1 font-normal">
                        <Calendar className="h-3 w-3" />
                        Início: {formatDateOnly(okr.data_inicio)}
                      </Badge>
                    )}
                    {okr.data_fim && (() => {
                      const deadlineStatus = getDeadlineStatus(okr.data_fim);
                      const deadlineStyle = getDeadlineBadgeStyle(deadlineStatus);
                      return (
                        <Badge variant="outline" className={cn('text-xs gap-1 font-normal', deadlineStyle)}>
                          <Clock className="h-3 w-3" />
                          Fim: {formatDateOnly(okr.data_fim)}
                          {deadlineStatus === 'overdue' && ' (Vencido)'}
                          {deadlineStatus === 'warning' && ' (Próximo)'}
                        </Badge>
                      );
                    })()}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progresso</span>
                    <span className="font-medium">{okr.progresso_geral}%</span>
                  </div>
                  <Progress value={okr.progresso_geral} className={cn('h-2', getProgressIndicatorStyle(okr.progresso_geral))} />
                </div>
                <TooltipProvider delayDuration={500}>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); toggleExpanded(okr.id); }}>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{isExpanded ? 'Ocultar Key Results' : 'Ver Key Results'}</TooltipContent>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onAddKR(okr.id); }}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Adicionar Key Result</TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onOpenHistorico(okr); }}>
                          <History className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver histórico</TooltipContent>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); onDeleteOKR(okr.id); }}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Excluir OKR</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              </div>

              {isExpanded && (
                <div className="pt-4 border-t border-border space-y-3 animate-fade-in" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-sm">Key Results ({krs.length})</h4>
                    {isAdmin && (
                      <Button variant="outline" size="sm" onClick={() => onAddKR(okr.id)}>
                        <Plus className="h-3 w-3 mr-1" />
                        Adicionar KR
                      </Button>
                    )}
                  </div>

                  {krs.map((kr, index) => {
                    const priority = getPriorityLabel(index);
                    const approvalBadge = getApprovalStatusBadge(kr);
                    const isPending = kr.aprovacao?.status === 'pending';
                    const canRequestCompletion = !isAdmin && !isPending;

                    return (
                      <div
                        key={kr.id}
                        className={cn('p-3 rounded-lg bg-muted/50 space-y-2', isAdmin && 'cursor-pointer')}
                        onClick={isAdmin ? () => onEditKR(kr, okr.id) : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {priority && (
                                <Badge className={cn('text-xs', priority.className)}>{priority.label}</Badge>
                              )}
                              {kr.prazo && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {formatDateOnly(kr.prazo)}
                                </Badge>
                              )}
                              {kr.anexo_obrigatorio && (
                                <Badge variant="outline" className="text-xs gap-1 text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400">
                                  <Paperclip className="h-3 w-3" />
                                  Anexo obrigatório
                                </Badge>
                              )}
                              {approvalBadge}
                            </div>
                            <p className="text-sm">{kr.descricao}</p>
                            {kr.aprovacao?.status === 'rejected' && kr.aprovacao.motivo_rejeicao && (
                              <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                                Motivo: {kr.aprovacao.motivo_rejeicao}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {canRequestCompletion && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs gap-1 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                                onClick={(e) => { e.stopPropagation(); onRequestCompletion(kr); }}
                                title="Solicitar conclusão"
                              >
                                <Send className="h-3 w-3" />
                                Atualizar progresso
                              </Button>
                            )}
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); onReorderKR(okr.id, kr.id, 'up'); }}
                                  disabled={index === 0}
                                  title="Mover para cima"
                                >
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); onReorderKR(okr.id, kr.id, 'down'); }}
                                  disabled={index === krs.length - 1}
                                  title="Mover para baixo"
                                >
                                  <ArrowDown className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={(e) => { e.stopPropagation(); onDeleteKR(kr.id); }}
                                >
                                  <Trash2 className="h-3 w-3 text-destructive" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <Progress value={kr.percentual} className={cn('flex-1 h-1.5', getProgressIndicatorStyle(kr.percentual))} />
                          <span className="text-xs text-muted-foreground w-24 text-right">
                            {kr.progresso_atual} / {kr.progresso_meta} ({kr.percentual}%)
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {krs.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">Nenhum Key Result cadastrado</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
